/**
 * useYjsProvider - React hook for Yjs WebSocket synchronization
 *
 * Connects Y.Doc to server via WebSocket with:
 * - Binary y-protocols for efficient sync
 * - Exponential backoff reconnection
 * - Connection status tracking
 * - Automatic cleanup on unmount
 *
 * AUTHENTICATION: WebSocket uses session cookies for auth.
 * The session cookie is sent automatically with the upgrade request.
 * Worker middleware authenticates and adds user context headers to DO request.
 *
 * @param ydoc - The Y.Doc to sync
 * @param boardId - Board identifier for WebSocket URL
 * @returns { status, connect, disconnect }
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as Y from 'yjs';
import * as syncProtocol from 'y-protocols/sync';
import * as awarenessProtocol from 'y-protocols/awareness';
import { encoding, decoding } from 'lib0';
import type { ConnectionStatus } from '@deep-agents/shared-types';
import { getApiWebSocketOrigin } from '../lib/api-base';

const DEBUG_COLLAB = (() => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem('DEBUG_COLLAB') === '1';
  } catch {
    return false;
  }
})();

const debugLog = (...args: any[]) => {
  if (DEBUG_COLLAB) {
    console.log(...args);
  }
};

const makeThrottledLogger = (intervalMs: number) => {
  let last = 0;
  let suppressed = 0;
  return (...args: any[]) => {
    if (!DEBUG_COLLAB) return;
    const now = Date.now();
    if (now - last >= intervalMs) {
      if (suppressed > 0) {
        console.log(...args, `(+${suppressed} suppressed)`);
      } else {
        console.log(...args);
      }
      last = now;
      suppressed = 0;
    } else {
      suppressed++;
    }
  };
};

const logRemoteUpdate = makeThrottledLogger(2000);
const logLocalUpdate = makeThrottledLogger(2000);

interface UseYjsProviderReturn {
  // Current connection status
  status: ConnectionStatus;
  // The awareness instance for presence
  awareness: awarenessProtocol.Awareness;
  // Manual reconnect (after failures)
  reconnect: () => void;
  // WebSocket ref for AI requests (Phase 7)
  wsRef: React.RefObject<WebSocket | null>;
}

// WebSocket URL configuration
// In development, connect directly to worker (Vite proxy doesn't handle WebSocket well)
// In production, use same origin
const getWebSocketUrl = (boardId: string): string => {
  const isDev = import.meta.env.DEV;

  if (isDev) {
    // Development: connect directly to worker at port 8787
    return `ws://localhost:8787/api/boards/${boardId}/ws`;
  }

  // Production: prefer API origin if configured, else same origin
  const apiOrigin = getApiWebSocketOrigin();
  if (apiOrigin) {
    const apiUrl = new URL(apiOrigin);
    const wsProtocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${apiUrl.host}/api/boards/${boardId}/ws`;
  }

  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${wsProtocol}//${window.location.host}/api/boards/${boardId}/ws`;
};

export function useYjsProvider(
  ydoc: Y.Doc,
  boardId: string
): UseYjsProviderReturn {
  const [status, setStatus] = useState<ConnectionStatus>('connecting');

  // WebSocket ref (stable across renders)
  const wsRef = useRef<WebSocket | null>(null);

  // Reconnection state
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Awareness instance (stable reference)
  const awarenessRef = useRef<awarenessProtocol.Awareness | null>(null);
  if (!awarenessRef.current) {
    awarenessRef.current = new awarenessProtocol.Awareness(ydoc);
  }

  // Track if we've completed initial sync
  const syncedRef = useRef(false);

  // Calculate backoff delay with jitter (per RESEARCH.md)
  const getBackoffDelay = useCallback(() => {
    const baseDelay = 100;
    const maxDelay = 2500;
    const exponentialDelay = Math.min(
      baseDelay * Math.pow(2, reconnectAttemptsRef.current),
      maxDelay
    );
    const jitter = exponentialDelay * Math.random() * 0.25;
    return exponentialDelay + jitter;
  }, []);

  // Encode message for Yjs protocols
  const encodeMessage = useCallback((protocol: number, messageType: number, data: Uint8Array): Uint8Array => {
    const encoder = encoding.createEncoder();
    encoding.writeVarUint(encoder, protocol);
    encoding.writeVarUint(encoder, messageType);
    encoding.writeVarUint8Array(encoder, data);
    return encoding.toUint8Array(encoder);
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    setStatus('connecting');

    // Get WebSocket URL (direct to worker in dev, same origin in prod)
    const wsUrl = getWebSocketUrl(boardId);

    debugLog('[useYjsProvider] Connecting to:', wsUrl);

    // NOTE: WebSocket upgrade request automatically includes cookies
    // The Worker's session middleware authenticates using the session cookie
    const ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = () => {
      debugLog('[useYjsProvider] WebSocket connected');
      setStatus('connected');
      reconnectAttemptsRef.current = 0;
      syncedRef.current = false;

      // Send our state vector to request missing updates
      const stateVector = Y.encodeStateVector(ydoc);
      const syncMessage = encodeMessage(0, 0, stateVector);
      ws.send(syncMessage);

      // Send initial awareness
      const awareness = awarenessRef.current!;
      const awarenessUpdate = awarenessProtocol.encodeAwarenessUpdate(awareness, [ydoc.clientID]);
      const awarenessMessage = encodeMessage(1, 0, awarenessUpdate);
      ws.send(awarenessMessage);
    };

    ws.onmessage = (event) => {
      const data = new Uint8Array(event.data);
      const decoder = decoding.createDecoder(data);
      const protocol = decoding.readVarUint(decoder);

      if (protocol === 0) {
        // SYNC protocol
        const messageType = decoding.readVarUint(decoder);
        const payload = decoding.readVarUint8Array(decoder);

        if (messageType === 0) {
          // SyncStep1: Server sends state vector
          const update = Y.encodeStateAsUpdate(ydoc, payload);
          const response = encodeMessage(0, 1, update);
          ws.send(response);
        } else if (messageType === 1) {
          // SyncStep2: Server sends updates we're missing
          debugLog('[useYjsProvider] Applying SyncStep2 update with origin: remote');
          Y.applyUpdate(ydoc, payload, 'remote');
          syncedRef.current = true;
        } else if (messageType === 2) {
          // Update: Incremental update from other client
          logRemoteUpdate('[useYjsProvider] Applying remote update with origin: remote');
          Y.applyUpdate(ydoc, payload, 'remote');
        }
      } else if (protocol === 1) {
        // AWARENESS protocol
        // Server sends: protocol, messageType, payload - must read messageType first
        const messageType = decoding.readVarUint(decoder);
        if (messageType === 0) {
          const payload = decoding.readVarUint8Array(decoder);
          awarenessProtocol.applyAwarenessUpdate(awarenessRef.current!, payload, ws);
        }
      }
    };

    ws.onclose = (event) => {
      debugLog('[useYjsProvider] WebSocket closed:', event.code, event.reason);
      wsRef.current = null;

      // Show disconnect after 5 seconds (per CONTEXT.md)
      const disconnectTimeout = setTimeout(() => {
        if (!wsRef.current) {
          setStatus('disconnected');
        }
      }, 5000);

      // Attempt reconnection
      const delay = getBackoffDelay();
      reconnectAttemptsRef.current++;

      // Cap at 50 attempts, then go read-only
      if (reconnectAttemptsRef.current >= 50) {
        clearTimeout(disconnectTimeout);
        setStatus('readonly');
        debugLog('[useYjsProvider] Max reconnect attempts, entering read-only mode');
        return;
      }

      debugLog(`[useYjsProvider] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);
      reconnectTimeoutRef.current = setTimeout(() => {
        clearTimeout(disconnectTimeout);
        connect();
      }, delay);
    };

    ws.onerror = (error) => {
      if (wsRef.current !== ws) {
        return;
      }
      if (ws.readyState === WebSocket.CLOSING || ws.readyState === WebSocket.CLOSED) {
        return;
      }
      console.warn('[useYjsProvider] WebSocket error:', {
        url: wsUrl,
        readyState: ws.readyState,
        error,
      });
    };
  }, [boardId, ydoc, encodeMessage, getBackoffDelay]);

  // Disconnect
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  // Listen for local updates and send to server
  useEffect(() => {
    const handleUpdate = (update: Uint8Array, origin: unknown) => {
      // Don't send remote updates back to server
      if (origin === 'remote') return;

      logLocalUpdate('[useYjsProvider] Local update detected, origin:', origin, 'size:', update.length);

      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        const message = encodeMessage(0, 2, update);
        ws.send(message);
        debugLog('[useYjsProvider] Sent update to server');
      } else {
        console.warn('[useYjsProvider] Cannot send update - WebSocket not open, state:', ws?.readyState);
      }
    };

    ydoc.on('update', handleUpdate);
    return () => {
      ydoc.off('update', handleUpdate);
    };
  }, [ydoc, encodeMessage]);

  // Listen for awareness changes and broadcast
  useEffect(() => {
    const awareness = awarenessRef.current!;

    const handleAwarenessChange = ({ added, updated, removed }: {
      added: number[];
      updated: number[];
      removed: number[];
    }) => {
      const changedClients = added.concat(updated).concat(removed);
      const ws = wsRef.current;

      // Only broadcast our own awareness changes
      if (changedClients.includes(ydoc.clientID) && ws && ws.readyState === WebSocket.OPEN) {
        const update = awarenessProtocol.encodeAwarenessUpdate(awareness, [ydoc.clientID]);
        const message = encodeMessage(1, 0, update);
        ws.send(message);
      }
    };

    awareness.on('change', handleAwarenessChange);
    return () => {
      awareness.off('change', handleAwarenessChange);
    };
  }, [ydoc, encodeMessage]);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    if (!boardId) {
      setStatus('disconnected');
      return;
    }
    connect();
    return () => {
      disconnect();
    };
  }, [boardId, connect, disconnect]);

  // Keep awareness alive with periodic updates (per RESEARCH.md - 30s timeout)
  useEffect(() => {
    const interval = setInterval(() => {
      const awareness = awarenessRef.current;
      const ws = wsRef.current;
      if (awareness && ws && ws.readyState === WebSocket.OPEN) {
        // Re-broadcast our awareness state
        const update = awarenessProtocol.encodeAwarenessUpdate(awareness, [ydoc.clientID]);
        const message = encodeMessage(1, 0, update);
        ws.send(message);
      }
    }, 20000); // Every 20 seconds (before 30s timeout)

    return () => clearInterval(interval);
  }, [ydoc, encodeMessage]);

  return {
    status,
    awareness: awarenessRef.current!,
    reconnect: connect,
    wsRef,
  };
}
